import React, { Component, Suspense } from 'react';
import {
  arrayOf, bool, func, shape, string
} from 'prop-types';
import classNames from 'classnames';
import { Favorite } from '@thd-olt-component-react/save-to-favorites';
import { Image } from '@thd-olt-component-react/core-ui';
import Helper from '../../Helper';
import '../../media-gallery.style.scss';
import './media-gallery-mobile.style.scss';
import { thumbnailClick, mediaClick } from '../../../publisher';

const MediaGalleryOverlayMobile = React.lazy(() => import(/* webpackChunkName: "media-gallery-overlay-mobile" */'../MediaGalleryOverlayMobile'));

class MediaGalleryMobile extends Component {

  constructor(props) {
    super(props);
    this.state = {
      mainImage: null,
      selectedMainImageIndex: 0,
      selectedThumbnailIndex: null,
      overlayOpened: false,
      thumbnails: props?.media?.thumbnails
    };
  }

  componentDidUpdate(prevProps) {
    const { media = {}, product } = this.props;
    const { main = [] } = media;
    if (prevProps?.product.itemId !== product.itemId
      || (prevProps.media && prevProps.media.main[0] !== main[0])) {
      this.updatePageLayout();
    }
  }

  onMainImageClick = (images, index, thumbnailType) => {
    const { thumbnails } = this.state;
    const payload = {
      eventName: 'expanded-view',
      primaryCategory: 'overlay'
    };
    thumbnailClick(payload);

    const thumbnailPayload = {
      section: 'media',
      component: 'thumbnails',
      target: thumbnailType || 'n/a',
      position: 'n/a'
    };

    mediaClick(thumbnailPayload);

    this.setState({
      overlayOpened: true, mainImage: images[index], selectedMainImageIndex: index, thumbnails, selectedThumbnailIndex: index
    });
    if (typeof window !== 'undefined') {
      window.history.pushState({ page: 'MEDIAOVERLAY' }, null, '');
    }
  };

  setChildrenElements = (images) => {
    const { product, overrideData, showFavorites, firstItemFetchPriority } = this.props;
    const brandName = overrideData?.brandNameLabel || product.identifiers?.brandName;
    const galleryLabel = overrideData?.galleryLabel || product.identifiers?.productLabel;
    const children = (images || []).map((image, imageIndex) => {
      const {
        collection, collectionId, mediaStill, url, videoStill, type
      } = image;
      const isImage360 = Helper.isImage360({ media: image });
      const isVideo = Helper.isVideo({ media: image });
      const isAR = Helper.isAR({ media: image });
      const isHotspot = Helper.isHotspot({ media: image });
      const lazy = imageIndex > 1;
      let thumbnailUrl = image?.url;
      if (isVideo) {
        thumbnailUrl = videoStill;
      } else if (isImage360) {
        thumbnailUrl = `${url}?thumb&profile=400`;
      } else if (isAR) {
        thumbnailUrl = mediaStill;
      }

      return (
        <li className="media-gallery-mobile" key={imageIndex}>
          {showFavorites && (
            <Favorite
              className="media-gallery-favorite"
              collectionId={collectionId}
              collectionImage={url}
              position="topRight"
              showCircle
            />
          )}
          <button className="media-gallery-mobile__thumbnails-wrapper" onClick={() => this.onMainImageClick(images, imageIndex, type)} type="button">
            {isImage360
              && (
                <>
                  <span className="background-circle background-circle--main" />
                  <span className="threesixty-icon threesixty-icon--main" />
                </>
              )}
            {isVideo
              && (
                <>
                  <span className="background-circle background-circle--main" />
                  <span className="play-icon play-icon--main" />
                </>
              )}
            {isAR
              && (
                <>
                  <span className="background-circle background-circle--main" />
                  <span className="ar-icon ar-icon--main" />
                </>
              )}
            {isHotspot
              && (
                <>
                  <span className="background-circle background-circle--main" />
                  <span className="hotspot-tag-icon hotspot-tag-icon__main" />
                </>
              )}
            <Image
              className="media-gallery-mobile__thumbnails"
              src={thumbnailUrl}
              alt={`${brandName} ${galleryLabel}`}
              lazy={lazy}
              height="263"
              width="263"
              fetchPriority={imageIndex === 0 ? firstItemFetchPriority : null}
            />
            {collection
              && (
                <div className="shopThisRoomContainer">
                  <a className="shopThisRoomLink" href={collection} onClick={(e) => e && e.stopPropagation()}> Shop This Room </a>
                </div>
              )}
          </button>
        </li>
      );
    });
    return children;
  };

  handleMediaClick = ({ media }) => {
    const { onMediaClick } = this.props;
    if (onMediaClick) {
      onMediaClick({ media });
    }
  };

  updatePageLayout = () => {
    const { media } = this.props;
    const { main, thumbnails } = media;
    this.setState({
      mainImage: main[0],
      selectedMainImageIndex: 0,
      thumbnails
    });
    this.setChildrenElements(main);
  };

  hideOverlay = () => {
    this.setState({ overlayOpened: false });
  };

  render() {
    const {
      overlayOpened, thumbnails, mainImage, selectedMainImageIndex, selectedThumbnailIndex
    } = this.state;
    const { product, media, overrideData } = this.props;
    const { main = [], zoom = [] } = media;
    const wrapperClass = classNames(
      'media-gallery-mobile__wrapper',
      { 'single-image': main.length === 1 }
    );

    return (
      <>
        <ul data-component="MediaGallery" className={wrapperClass}>
          {this.setChildrenElements(main)}
        </ul>
        {overlayOpened
          && (
            <Suspense fallback={<div />}>
              <MediaGalleryOverlayMobile
                handleClose={this.hideOverlay}
                overlayOpened={overlayOpened}
                product={product}
                mainImage={mainImage}
                mainImages={main}
                overrideData={overrideData}
                onMediaClick={this.handleMediaClick}
                zoomedImages={zoom}
                thumbnails={thumbnails}
                selectedMainImageIndex={selectedMainImageIndex}
                selectedThumbnailIndex={selectedThumbnailIndex}
              />
            </Suspense>
          )}
      </>
    );
  }
}

MediaGalleryMobile.propTypes = {
  media: shape({
    main: arrayOf(shape({
      id: string,
      location: string,
      mediaType: string
    })),
    thumbnails: arrayOf(shape({
      id: string,
      location: string,
      mediaType: string
    })),
    zoom: arrayOf(shape({
      id: string,
      location: string,
      mediaType: string
    }))
  }).isRequired,
  overrideData: shape({
    galleryLabel: string,
    brandNameLabel: string
  }),
  onMediaClick: func,
  product: shape({}),
  showFavorites: bool,
  firstItemFetchPriority: string
};

MediaGalleryMobile.defaultProps = {
  onMediaClick: null,
  product: {},
  showFavorites: false,
  overrideData: null,
  firstItemFetchPriority: ''
};

export { MediaGalleryMobile };
